/**
 * Google Places Autocomplete input for venue address.
 * Requires REACT_APP_GOOGLE_MAPS_API_KEY. On place select, calls onSelect
 * with { formattedAddress, latitude, longitude, placeId }.
 * Without the key, renders a plain text input (manual address only).
 */

import React, { useEffect, useRef, useState } from 'react';
import { getGoogleMapsApiKey, isPlacesAutocompleteAvailable, warnIfGoogleMapsKeyMissing } from '../config/placesConfig';
import { fetchPublicEnv } from '../config/runtimeEnv';

let scriptLoadPromise = null;

function loadGoogleMapsScript(key) {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => {
      scriptLoadPromise = null;
      console.error(
        '[PlaceAutocomplete] Google Maps script failed to load. Enable "Maps JavaScript API" and "Places API" in Google Cloud Console, check key restrictions, and ensure REACT_APP_GOOGLE_MAPS_API_KEY is set. Restart the dev server after changing .env (npm run start).'
      );
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

export function isPlacesAvailable() {
  return isPlacesAutocompleteAvailable();
}

/**
 * @param {object} props
 * @param {string} [props.value] - Controlled input value (address string)
 * @param {function(string)} [props.onChange] - Called when input text changes
 * @param {function({ formattedAddress: string, latitude: number|null, longitude: number|null, placeId: string|null })} props.onSelect - Called when user selects a place
 * @param {string} [props.placeholder]
 * @param {string} [props.className]
 * @param {string} [props.id]
 * @param {boolean} [props.disabled]
 * @param {object} [props.inputStyle]
 */
export default function PlaceAutocomplete({
  value = '',
  onChange,
  onSelect,
  placeholder = 'Search address or place…',
  className = '',
  id,
  disabled = false,
  inputStyle,
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const [scriptReady, setScriptReady] = useState(false);
  const [configFetched, setConfigFetched] = useState(false);
  const key = getGoogleMapsApiKey();

  onSelectRef.current = onSelect;

  // If key is missing, fetch runtime config (Firestore config/publicEnv) so key from there is available
  useEffect(() => {
    if (key) return;
    fetchPublicEnv().then(() => setConfigFetched(true));
  }, [key]);

  useEffect(() => {
    if (!key) {
      setScriptReady(false);
      return;
    }
    loadGoogleMapsScript(key)
      .then(() => setScriptReady(true))
      .catch(() => setScriptReady(false));
  }, [key, configFetched]);

  // Dev hint when key is missing (e.g. in .env but server not restarted)
  useEffect(() => {
    if (key) return;
    warnIfGoogleMapsKeyMissing();
  }, [key]);

  useEffect(() => {
    if (!scriptReady || !key || !window.google?.maps?.places) return;
    const inputEl = inputRef.current;
    if (!inputEl || autocompleteRef.current) return; // already attached or no input yet

    // Attach after input is in DOM (next tick) so Autocomplete binds correctly
    const id = setTimeout(() => {
      if (!inputRef.current || autocompleteRef.current) return;
      const Autocomplete = window.google.maps.places.Autocomplete;
      const autocomplete = new Autocomplete(inputRef.current, {
        types: ['establishment', 'geocode'],
        fields: ['formatted_address', 'place_id', 'geometry'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const cb = onSelectRef.current;
        if (!place || !cb) return;
        const formattedAddress = (place.formatted_address || '').trim() || null;
        let latitude = null;
        let longitude = null;
        if (place.geometry?.location) {
          latitude = place.geometry.location.lat();
          longitude = place.geometry.location.lng();
        }
        const placeId = (place.place_id || '').trim() || null;
        cb({ formattedAddress, latitude, longitude, placeId });
      });

      autocompleteRef.current = autocomplete;
    }, 0);

    return () => {
      clearTimeout(id);
      const ac = autocompleteRef.current;
      autocompleteRef.current = null;
      if (window.google?.maps?.event && ac && ac.addListener) {
        try {
          window.google.maps.event.clearInstanceListeners(ac);
        } catch (_) {}
      }
    };
  }, [scriptReady, key]);

  const hasPlaces = !!key;

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange && onChange(e.target.value)}
      placeholder={hasPlaces ? placeholder : 'Enter address manually'}
      className={className}
      id={id}
      disabled={disabled}
      style={{ maxWidth: '100%', ...inputStyle }}
      autoComplete="off"
      aria-label="Address or place for maps and calendar"
    />
  );
}
